/**
 * Landing Target Audience organism component
 * Showcases the different user types who benefit from Furniturr
 */

// #region Imports
import {
    Box,
    Typography,
    Container,
    useTheme,
    Card,
    CardContent,
    Avatar,
    Stack
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
    Business,
    Handyman,
    Architecture,
    School
} from "@mui/icons-material";
// #endregion

// #region Types
/**
 * Audience segment interface
 */
interface AudienceSegment {
    id: string;
    icon: React.ReactNode;
    title: string;
    description: string;
    useCase: string;
    benefits: string[];
}

/**
 * Props for LandingTargetAudience organism component
 */
interface LandingTargetAudienceOrganismProps {
    /** Optional test ID for testing purposes */
    "data-testid"?: string;
}
// #endregion

// #region Data
// Audience segments are now dynamically loaded from translations in the component
// #endregion

// #region Component
/**
 * LandingTargetAudience organism component showcasing different user segments
 * Displays audience segments with their specific use cases and benefits
 *
 * @param props - The component props
 * @returns JSX element representing the target audience section
 */
function LandingTargetAudienceOrganism({ ...props }: LandingTargetAudienceOrganismProps) {
    const { t } = useTranslation('landing');
    const theme = useTheme();

    // Dynamic audience data from translations
    const AUDIENCE_SEGMENTS: AudienceSegment[] = [
        {
            id: "manufacturers",
            icon: <Business />,
            title: t('targetAudience.segments.manufacturers.title'),
            description: t('targetAudience.segments.manufacturers.description'),
            useCase: t('targetAudience.segments.manufacturers.useCase'),
            benefits: t('targetAudience.segments.manufacturers.benefits', { returnObjects: true }) as string[]
        },
        {
            id: "craftsmen",
            icon: <Handyman />,
            title: t('targetAudience.segments.craftsmen.title'),
            description: t('targetAudience.segments.craftsmen.description'),
            useCase: t('targetAudience.segments.craftsmen.useCase'),
            benefits: t('targetAudience.segments.craftsmen.benefits', { returnObjects: true }) as string[]
        },
        {
            id: "designers",
            icon: <Architecture />,
            title: t('targetAudience.segments.designers.title'),
            description: t('targetAudience.segments.designers.description'),
            useCase: t('targetAudience.segments.designers.useCase'),
            benefits: t('targetAudience.segments.designers.benefits', { returnObjects: true }) as string[]
        },
        {
            id: "institutions",
            icon: <School />,
            title: t('targetAudience.segments.institutions.title'),
            description: t('targetAudience.segments.institutions.description'),
            useCase: t('targetAudience.segments.institutions.useCase'),
            benefits: t('targetAudience.segments.institutions.benefits', { returnObjects: true }) as string[]
        }
    ];

    return (
        <Box
            component="section"
            role="region"
            aria-labelledby="audience-title"
            id="target-audience"
            sx={{
                py: { xs: 8, md: 12 },
                backgroundColor: "background.default",
                position: "relative",
            }}
            {...props}
        >
            <Container maxWidth="lg">
                {/* Section Header */}
                <Box sx={{ textAlign: "center", mb: 8 }}>
                    <Typography
                        variant="overline"
                        sx={[
                            {
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                letterSpacing: 1,
                                mb: 2,
                                display: "block",
                            },
                            (theme) =>
                                theme.applyStyles("dark", {
                                    color: "rgba(255, 255, 255, 0.6)",
                                }),
                        ]}
                    >
                        {t('targetAudience.sectionTitle')}
                    </Typography>
                    <Typography
                        variant="h2"
                        component="h2"
                        id="audience-title"
                        sx={{
                            mb: 3,
                            fontSize: { xs: "2rem", md: "3rem" },
                            fontWeight: 700,
                        }}
                    >
                        {t('targetAudience.title.main')}{" "}
                        <Box component="span" sx={{ color: theme.palette.secondary.main }}>
                            {t('targetAudience.title.accent')}
                        </Box>
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={[
                            {
                                color: theme.palette.text.secondary,
                                maxWidth: 600,
                                mx: "auto",
                                lineHeight: 1.6,
                            },
                            (theme) =>
                                theme.applyStyles("dark", {
                                    color: "rgba(255, 255, 255, 0.8)",
                                }),
                        ]}
                    >
                        {t('targetAudience.subtitle')}
                    </Typography>
                </Box>

                {/* Audience Grid */}
                <Box
                    role="list"
                    aria-label="Target audience segments"
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "repeat(2, 1fr)"
                        },
                        gap: 4,
                    }}
                >
                    {AUDIENCE_SEGMENTS.map((segment, index) => (
                        <Card
                            key={segment.id}
                            role="listitem"
                            tabIndex={0}
                            aria-labelledby={`audience-title-${segment.id}`}
                            aria-describedby={`audience-desc-${segment.id} audience-usecase-${segment.id} audience-benefits-${segment.id}`}
                            sx={{
                                height: "100%",
                                transition: "all 0.3s ease",
                                border: 1,
                                borderColor: "divider",
                                position: "relative",
                                "&:hover, &:focus": {
                                    transform: "translateY(-4px)",
                                    boxShadow: theme.shadows[8],
                                    borderColor: theme.palette.primary.main,
                                    outline: "2px solid",
                                    outlineColor: theme.palette.primary.main,
                                    outlineOffset: "2px",
                                    "& .audience-avatar": {
                                        transform: "scale(1.05)",
                                    },
                                },
                            }}
                        >
                            <CardContent sx={{ p: 4, height: "100%", display: "flex", flexDirection: "column" }}>
                                {/* Header */}
                                <Stack direction="row" spacing={3} alignItems="flex-start" sx={{ mb: 3 }}>
                                    <Avatar
                                        className="audience-avatar"
                                        aria-hidden="true"
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            backgroundColor: theme.palette.primary.main,
                                            color: theme.palette.primary.contrastText,
                                            transition: "transform 0.3s ease",
                                            "& .MuiSvgIcon-root": {
                                                fontSize: "2rem",
                                            },
                                        }}
                                    >
                                        {segment.icon}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            component="h3"
                                            id={`audience-title-${segment.id}`}
                                            sx={[
                                                {
                                                    mb: 1,
                                                    fontWeight: 600,
                                                    color: theme.palette.text.primary,
                                                },
                                                (theme) =>
                                                    theme.applyStyles("dark", {
                                                        color: "#FFFFFF",
                                                    }),
                                            ]}
                                        >
                                            {segment.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            id={`audience-desc-${segment.id}`}
                                            sx={[
                                                {
                                                    color: theme.palette.text.secondary,
                                                    lineHeight: 1.5,
                                                },
                                                (theme) =>
                                                    theme.applyStyles("dark", {
                                                        color: "rgba(255, 255, 255, 0.8)",
                                                    }),
                                            ]}
                                        >
                                            {segment.description}
                                        </Typography>
                                    </Box>
                                </Stack>

                                {/* Use Case */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={[
                                            {
                                                color: theme.palette.primary.main,
                                                fontWeight: 600,
                                                mb: 1,
                                            },
                                            (theme) =>
                                                theme.applyStyles("dark", {
                                                    color: "rgba(255, 255, 255, 0.9)",
                                                }),
                                        ]}
                                    >
                                        {t('targetAudience.useCaseLabel')}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        id={`audience-usecase-${segment.id}`}
                                        sx={[
                                            {
                                                color: theme.palette.text.secondary,
                                                fontStyle: "italic",
                                                lineHeight: 1.5,
                                            },
                                            (theme) =>
                                                theme.applyStyles("dark", {
                                                    color: "rgba(255, 255, 255, 0.8)",
                                                }),
                                        ]}
                                    >
                                        {segment.useCase}
                                    </Typography>
                                </Box>

                                {/* Benefits */}
                                <Box
                                    id={`audience-benefits-${segment.id}`}
                                    sx={{ flexGrow: 1 }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={[
                                            {
                                                color: theme.palette.primary.main,
                                                fontWeight: 600,
                                                mb: 2,
                                            },
                                            (theme) =>
                                                theme.applyStyles("dark", {
                                                    color: "rgba(255, 255, 255, 0.9)",
                                                }),
                                        ]}
                                    >
                                        {t('targetAudience.benefitsLabel')}
                                    </Typography>
                                    <Stack
                                        component="ul"
                                        spacing={1}
                                        role="list"
                                        aria-label="Benefits for this audience"
                                        sx={{ listStyle: "none", p: 0, m: 0 }}
                                    >
                                        {segment.benefits.map((benefit, benefitIndex) => (
                                            <Typography
                                                key={benefitIndex}
                                                component="li"
                                                variant="body2"
                                                role="listitem"
                                                sx={[
                                                    {
                                                        color: theme.palette.text.secondary,
                                                        display: "flex",
                                                        alignItems: "flex-start",
                                                        "&::before": {
                                                            content: '"•"',
                                                            color: theme.palette.secondary.main,
                                                            fontWeight: "bold",
                                                            marginRight: 1,
                                                            flexShrink: 0,
                                                            "ariaHidden": "true",
                                                        },
                                                    },
                                                    (theme) =>
                                                        theme.applyStyles("dark", {
                                                            color: "rgba(255, 255, 255, 0.8)",
                                                        }),
                                                ]}
                                            >
                                                {benefit}
                                            </Typography>
                                        ))}
                                    </Stack>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>

                {/* Bottom Message */}
                <Box sx={{ textAlign: "center", mt: 8 }}>
                    <Typography
                        variant="h6"
                        sx={[
                            {
                                color: theme.palette.text.primary,
                                fontWeight: 500,
                                mb: 2,
                            },
                            (theme) =>
                                theme.applyStyles("dark", {
                                    color: "#FFFFFF",
                                }),
                        ]}
                    >
                        {t('targetAudience.bottomMessage.title')}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={[
                            {
                                color: theme.palette.text.secondary,
                                maxWidth: 500,
                                mx: "auto",
                                lineHeight: 1.6,
                            },
                            (theme) =>
                                theme.applyStyles("dark", {
                                    color: "rgba(255, 255, 255, 0.8)",
                                }),
                        ]}
                    >
                        {t('targetAudience.bottomMessage.description')}
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}
// #endregion

// #region Exports
export default LandingTargetAudienceOrganism;
export type { LandingTargetAudienceOrganismProps };
// #endregion
